import React from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import styles from '../../../styles';
import { ColumnBlock, ColumnContent } from '../../layout/components';
import { Icon } from '../../../icons';

const orbit = keyframes`
  from {
    transform: rotate(0deg) translate3d(0,0,0);;
  }

  to {
    transform: rotate(360deg) translate3d(0,0,0);
  }
`;

const StyledOpenPositionsBlock = styled(ColumnBlock)`
  margin-bottom: 3rem;
`;

const Ring = styled.figure`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  overflow: visible;
  padding: 2px;
  svg {
    overflow: visible;
    vector-effect: non-scaling-stroke;
    circle {
      fill: none;
      stroke: ${styles.color.SLATE_LIGHT};
      stroke-width: 1px;
      stroke-dasharray: 2.96 6.12725830078125;
      stroke-dashoffset: 0;
      transition-property: opacity;
      transition-duration: 0.75s;
      transition-timing-function: ${styles.easing.inOutQuad};
    }
  }
`;

const DotHover = styled.figure`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 2px;
  transform: rotate(${props => props.initialRotation}deg);
  svg {
    overflow: visible;
    vector-effect: non-scaling-stroke;
    animation: ${orbit} ${props => props.speed * 25}s infinite linear;
    circle {
      transform-origin: 50% 50%;
      transform-box: fill-box;
      fill: none;
      stroke: ${styles.color.ORANGE};
      stroke-width: 2px;

      stroke-dasharray: 0 1224.725830078125;
      stroke-dashoffset: 1224.725830078125;
      stroke-linecap: round;
      transition-property: transform, stroke-dasharray, stroke-dashoffset;
      transition-duration: 0.75s;
      transition-timing-function: ${styles.easing.inOutQuad};
    }
  }
`;

const Dot = styled.figure`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 2px;
  transform: rotate(${props => props.initialRotation}deg) translate3d(0,0,0);;
  transition-property: transform;
  transition-duration: 0.675s;
  transition-timing-function: ${styles.easing.inOutQuad};

  svg {
    overflow: visible;
    fill: ${styles.color.ORANGE};
    stroke: ${styles.color.WHITE};
    stroke-width: 2px;
    vector-effect: non-scaling-stroke;

    animation: ${orbit} ${props => props.speed * 25}s infinite linear;

    circle {
      transform-origin: 50% 50%;
      transform-box: fill-box;
      transition-property: transform;
      transition-duration: 0.75s;
      transition-timing-function: ${styles.easing.inOutQuad};
    }
  }
`;

const Content = styled.div`
  position: absolute;
  top: 1.875rem;
  left: 1.875rem;
  bottom: 5.75rem;
  right: 1.875rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  figure {
    color: ${styles.color.ORANGE};
    width: 4.5rem;
    height: 4.5rem;
    margin-bottom: 1.5rem;
    margin-top: 1.5rem;
  }

  h2 {
    font-size: 3rem;
    color: ${styles.color.ORANGE};
    margin-bottom: 0.5rem;
  }

  span {
    color: ${styles.color.SLATE};
    font-family: 'ApercuMono';
    font-size: 1.125rem;
    text-transform: uppercase;
  }
`;

const Wrapper = styled.div`
  cursor: pointer;
  position: relative;
  padding-top: 100%;
  width: 100%;

  .current-input-mouse &:hover,
  .current-input-touch &:active {
    ${Ring} {
      svg circle {
        opacity: 0;
      }
    }

    ${Dot} {
      transform: rotate(${props => props.hoverRotation}deg) translate3d(0,0,0);;
      svg {
        circle {
          transform: scale(0) translate3d(0,0,0);
        }
      }
    }
    ${DotHover} {
      svg {
        circle {
          transform: rotate(0deg) translate3d(0,0,0);
          stroke-dashoffset: 0;
          stroke-dasharray: 1224.725830078125 0;
        }
      }
    }
  }
`;

const OpenPositionsBlock = props => {
  const { category, slug, numPositions, totalPositions, linkTo } = props;

  const multiplier = Math.max(0, Math.min(1, numPositions / totalPositions));
  const minRadius = 6;
  const maxRadius = 12;
  const minSpeed = 0.5;
  const maxSpeed = 2.5;

  const radius = minRadius + multiplier * (maxRadius - minRadius);
  const speed = minSpeed + multiplier * (maxSpeed - minSpeed);

  const initialRotation = Math.floor(Math.random() * 360);
  const hoverRotation = initialRotation + 120;

  return (
    <StyledOpenPositionsBlock columns={1}>
      <ColumnContent>
        <NavLink to={linkTo}>
          <Wrapper hoverRotation={hoverRotation}>
            <Ring>
              <svg viewBox="0 0 390 390">
                <circle cx="195" cy="195" r="195" />
              </svg>
            </Ring>
            <Dot speed={speed} initialRotation={initialRotation}>
              <svg viewBox="0 0 390 390">
                <circle cx="390" cy="195" r={radius} />
              </svg>
            </Dot>
            <DotHover speed={speed} radius={radius} initialRotation={initialRotation}>
              <svg viewBox="0 0 390 390">
                <circle cx="195" cy="195" r="195" />
              </svg>
            </DotHover>
            <Content>
              <figure>
                <Icon icon={`#icon-careers-${slug}`} />
              </figure>
              <h2>{category}</h2>
            </Content>
          </Wrapper>
        </NavLink>
      </ColumnContent>
    </StyledOpenPositionsBlock>
  );
};

OpenPositionsBlock.propTypes = {
  linkTo: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  numPositions: PropTypes.number.isRequired,
  totalPositions: PropTypes.number.isRequired,
};

export default OpenPositionsBlock;



// WEBPACK FOOTER //
// ./src/modules/pages/components/OpenPositionsBlock.js